import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { useUserContext } from '../../contexts';
import { hasPremiumAssetsAccess } from '../../lib/subscriptionCapabilities';
import { getProductioncrateSiteHref, getUserAccountHref } from '../Header/accountIdentity';
import useMarketplaceCart from './useMarketplaceCart';
import MarketplaceCartSummary from './MarketplaceCartSummary';
import { navigateToMarketplaceCartSignup } from './checkoutFlow';
import '../../styles/layout.scss';
import './MarketplaceCart.scss';

const MarketplaceCartPage = () => {
    const { user } = useUserContext();
    const [searchParams] = useSearchParams();
    const { snapshot, controller } = useMarketplaceCart();
    const [checkoutPending, setCheckoutPending] = useState(false);
    const [autoCheckoutAttempted, setAutoCheckoutAttempted] = useState(false);
    const isGuest = !controller.session?.user && !controller.session?.isAuthenticating;
    const hasPremiumAssetDownloads = hasPremiumAssetsAccess(user?.team?.subscription?.plan);
    const myDownloadsHref = getProductioncrateSiteHref({ pathname: '/my-downloads' });
    const subscribeHref = getUserAccountHref({
        pathname: isGuest ? '/register/cart-subscribe' : '/upgrade?campaign=cart-subscribe',
    });
    const subscribeLabel = 'Unlimited access';
    const isReturnUnavailable = searchParams.get('intake') === 'return_unavailable';
    const shouldAutoCheckout = useMemo(
        () => String(searchParams.get('cart_intent') ?? '').trim().toLowerCase() === 'checkout',
        [searchParams]
    );

    const handleCheckout = async () => {
        if (isGuest) {
            navigateToMarketplaceCartSignup();
            return;
        }

        setCheckoutPending(true);
        try {
            await controller.checkout();
        } finally {
            setCheckoutPending(false);
        }
    };

    useEffect(() => {
        if (!shouldAutoCheckout || autoCheckoutAttempted || hasPremiumAssetDownloads || isGuest || !snapshot.isSessionResolved) {
            return;
        }

        let isCancelled = false;
        setAutoCheckoutAttempted(true);
        setCheckoutPending(true);

        controller.checkout().catch(() => {
            return false;
        }).finally(() => {
            if (!isCancelled) {
                setCheckoutPending(false);
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [
        autoCheckoutAttempted,
        controller,
        hasPremiumAssetDownloads,
        isGuest,
        shouldAutoCheckout,
        snapshot.isSessionResolved,
    ]);

    return (
        <div className="layout marketplace-cart-page">
            <Header />
            <main className="marketplace-cart-page__body">
                <section className="marketplace-cart-page__shell">
                    <div className="marketplace-cart-page__intro">
                        <div className="marketplace-cart-page__eyebrow">Marketplace cart</div>
                        <h1>Shopping Cart</h1>
                        <p>Buy lifetime access to individual assets and Plugins.</p>
                    </div>
                    {hasPremiumAssetDownloads &&
                    <section className="marketplace-cart-page__premium-state">
                        <h2>Your plan already includes premium asset downloads.</h2>
                        <p>Marketplace checkout is hidden for PRO and Enterprise members because individual asset purchases are not needed on this account.</p>
                        <a className="marketplace-cart-button is-primary" href={myDownloadsHref}>
                            Go to My Downloads
                        </a>
                    </section>
                    }
                    {!hasPremiumAssetDownloads &&
                    <MarketplaceCartSummary
                        assetInteractionMode="link-only"
                        checkoutPending={checkoutPending}
                        loginHref="/login?return_to=/marketplace/cart&cart_intent=checkout"
                        onCheckout={handleCheckout}
                        onRemove={(lineKey) => controller.removeItem(lineKey)}
                        signupHref="/register"
                        subscribeHref={subscribeHref}
                        subscribeLabel={subscribeLabel}
                        continueExploringHref={isReturnUnavailable
                            ? 'https://www.productioncrate.com/search/new?main_category=all'
                            : null}
                        snapshot={snapshot}
                    />
                    }
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default MarketplaceCartPage;
